import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatMyanmarDateTime } from '@/lib/dateUtils'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import SlideOver from '@/components/SlideOver'
import {
  Plus, Search, FileText, CheckSquare, Square, CheckCircle2, Download,
  Loader2, ClipboardCheck, AlertCircle, Check, X, Upload, Trash2,
  Link2, Building2, Paperclip
} from 'lucide-react'

const ADMIN_ROLES = ['super_admin', 'admin']
const ENGINEERING_ROLES = ['chief_engineer', 'operation_manager', 'engineer', 'technician']
const STATUSES = ['Draft', 'Pending', 'Approved', 'Rejected']

interface HandoverForm {
  id: number
  ho_number: string
  project_id: number | null
  project_name: string
  project_code: string
  project_type: string
  client_name: string
  client_id: number | null
  phone: string
  address: string
  engineer: string
  team_leader: string
  handover_date: string | null
  date_completed: string | null
  service_expiry_date: string | null
  assigned_to: string
  description: string
  manual_complete: boolean
  products_checklist: boolean
  customer_signed: boolean
  status: string
  notes: string
  documents: string
  created_at: string
}

interface ProjectOption {
  id: number
  project_code: string
  project_type: string
  client_name: string
  phone: string
  address: string
  description: string
  assigned_to: string
  team_members: any
}

interface HoAttachment {
  name: string
  url: string
  size: number
  type: string
  uploaded_at: string
  uploaded_by: string
  sharepoint_path: string
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700',
    Pending: 'bg-amber-100 text-amber-800',
    Approved: 'bg-green-100 text-green-800',
    Rejected: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-slate-100 text-slate-700'}`}>
      {status}
    </span>
  )
}

function CheckItem({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {checked ? <Check size={16} className="text-green-600 shrink-0" /> : <X size={16} className="text-red-500 shrink-0" />}
      <span className={`text-sm ${checked ? 'text-green-700' : 'text-slate-500 line-through'}`}>{label}</span>
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(type: string): boolean {
  return type?.startsWith('image/')
}

const BLANK_FORM = {
  project_id: null as number | null,
  project_name: '', project_code: '', project_type: 'CLAPJ', client_name: '', phone: '',
  address: '', engineer: '', team_leader: '', assigned_to: '',
  handover_date: '', date_completed: '', service_expiry_date: '',
  description: '', notes: '', manual_complete: false, products_checklist: false,
  customer_signed: false, status: 'Draft',
}

// --- Project Picker component ---
function ProjectPicker({ value, onSelect }: {
  value: { id: number | null; label: string }
  onSelect: (proj: ProjectOption | null) => void
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [debouncedQ, setDebouncedQ] = useState('')

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQ(query), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  const { data: results = [], isFetching } = useQuery<ProjectOption[]>({
    queryKey: ['handover-project-search', debouncedQ],
    queryFn: () => api.get('/handovers/projects/search', { params: { q: debouncedQ } }).then(r => r.data),
    enabled: focused,
    staleTime: 10000,
  })

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const inp = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700'

  if (value.id) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
        <Link2 size={14} className="text-red-700 shrink-0" />
        <span className="text-sm font-medium text-red-800 flex-1 truncate">{value.label}</span>
        <button type="button" onClick={() => { onSelect(null); setQuery('') }}
          className="text-red-400 hover:text-red-700 transition-colors">
          <X size={14} />
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className={`${inp} pl-8`}
          placeholder="Search by project code or client name…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => { setFocused(true); setOpen(true) }}
        />
        {isFetching && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {results.map(p => (
            <button
              key={p.id}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onSelect(p); setOpen(false); setFocused(false); setQuery('') }}
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 transition-colors border-b border-slate-50 last:border-0"
            >
              <div className="font-medium text-sm text-slate-900">
                {p.project_type}-{p.project_code}
              </div>
              <div className="text-xs text-slate-500 truncate">{p.client_name}</div>
            </button>
          ))}
        </div>
      )}
      {open && focused && !isFetching && results.length === 0 && query.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3">
          <p className="text-sm text-slate-400">No projects found</p>
        </div>
      )}
    </div>
  )
}

// --- Attachments section ---
function HandoverAttachments({ handoverId, canDelete }: { handoverId: number; canDelete: boolean }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const { data: files = [], isLoading } = useQuery<HoAttachment[]>({
    queryKey: ['handover-attachments', handoverId],
    queryFn: () => api.get(`/handovers/${handoverId}/attachments`).then(r => r.data),
    staleTime: 30000,
  })

  const deleteMutation = useMutation({
    mutationFn: (filename: string) => api.delete(`/handovers/${handoverId}/attachments/${encodeURIComponent(filename)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handover-attachments', handoverId] })
      setDeleteConfirm(null)
    },
  })

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError('')
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await api.post(`/handovers/${handoverId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      queryClient.invalidateQueries({ queryKey: ['handover-attachments', handoverId] })
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Paperclip size={14} className="text-slate-500" /> Attachments
          {files.length > 0 && <span className="text-xs text-slate-400 font-normal">({files.length})</span>}
        </h4>
        <label className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? 'Uploading…' : 'Upload'}
          <input ref={fileInputRef} type="file" className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      {uploadError && (
        <div className="mb-2 flex items-center gap-2 p-2 bg-red-50 rounded-lg text-red-700 text-xs">
          <AlertCircle size={12} /> {uploadError}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-700" />
        </div>
      ) : files.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-3">No files uploaded yet</p>
      ) : (
        <div className="space-y-2">
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg group">
              <div className="text-slate-400 shrink-0">
                {isImage(file.type) ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                ) : (
                  <FileText size={16} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{file.name}</div>
                <div className="text-xs text-slate-400">
                  {formatFileSize(file.size)}{file.uploaded_by ? ` · ${file.uploaded_by}` : ''}
                  {file.uploaded_at ? ` · ${formatMyanmarDateTime(file.uploaded_at)}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {file.url && (
                  <a href={file.url} target="_blank" rel="noopener noreferrer"
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded transition-colors">
                    <Download size={13} />
                  </a>
                )}
                {canDelete && (
                  deleteConfirm === file.name ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => deleteMutation.mutate(file.name)} disabled={deleteMutation.isPending}
                        className="px-2 py-0.5 text-xs bg-red-700 text-white rounded hover:bg-red-800">
                        {deleteMutation.isPending ? '…' : 'Yes'}
                      </button>
                      <button onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-0.5 text-xs border rounded hover:bg-slate-100">No</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(file.name)}
                      className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                      <Trash2 size={13} />
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2">Accepted: PDF, JPEG, PNG, DOC, DOCX, XLS, XLSX (max 10 MB)</p>
    </div>
  )
}

export default function HandoverForms() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = ADMIN_ROLES.includes(user?.role || '')
  const { canDo } = useMyPermissions()
  const canWrite = canDo('handover_write')

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<HandoverForm | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState<any>(BLANK_FORM)
  const [error, setError] = useState('')
  const [transferConfirm, setTransferConfirm] = useState(false)
  const [transferSuccess, setTransferSuccess] = useState('')
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['handovers', search, statusFilter, page],
    queryFn: () => api.get('/handovers', { params: { search, status: statusFilter, page, per_page: 25 } }).then(r => r.data),
    staleTime: 10000,
  })

  const createMutation = useMutation({
    mutationFn: (payload: any) => api.post('/handovers', payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['handovers'] })
      setShowCreate(false)
      setForm(BLANK_FORM)
      setSelected(res.data)
      setEditMode(false)
      setError('')
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to create handover'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => api.put(`/handovers/${id}`, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['handovers'] })
      setSelected(res.data)
      setEditMode(false)
      setError('')
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to update handover'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/handovers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handovers'] })
      setSelected(null)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to delete'),
  })

  const transferMutation = useMutation({
    mutationFn: (id: number) => api.post(`/handovers/${id}/transfer`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['handovers'] })
      setTransferConfirm(false)
      setTransferSuccess(res.data?.message || 'Project marked as Complete!')
    },
    onError: (err: any) => {
      setTransferConfirm(false)
      setError(err.response?.data?.detail || 'Failed to mark project as complete')
    },
  })

  const handleOpenCreate = () => {
    setForm(BLANK_FORM)
    setError('')
    setShowCreate(true)
  }

  const handleOpenEdit = () => {
    if (!selected) return
    setForm({
      project_id: selected.project_id || null,
      project_name: selected.project_name || '',
      project_code: selected.project_code || '',
      project_type: selected.project_type || 'CLAPJ',
      client_name: selected.client_name || '',
      phone: selected.phone || '',
      address: selected.address || '',
      engineer: selected.engineer || '',
      team_leader: selected.team_leader || '',
      assigned_to: selected.assigned_to || '',
      handover_date: selected.handover_date || '',
      date_completed: selected.date_completed || '',
      service_expiry_date: selected.service_expiry_date || '',
      description: selected.description || '',
      notes: selected.notes || '',
      manual_complete: selected.manual_complete || false,
      products_checklist: selected.products_checklist || false,
      customer_signed: selected.customer_signed || false,
      status: selected.status || 'Draft',
    })
    setError('')
    setEditMode(true)
  }

  const handleProjectSelect = (proj: ProjectOption | null) => {
    if (!proj) {
      setForm((prev: any) => ({
        ...prev,
        project_id: null,
        project_name: '',
        project_code: '',
        project_type: 'CLAPJ',
        client_name: '',
        phone: '',
        address: '',
        description: '',
        team_leader: '',
        engineer: '',
        assigned_to: '',
      }))
      return
    }
    let assignedTo = ''
    let teamLeader = ''
    let engineer = ''
    if (proj.assigned_to) {
      try {
        const a = typeof proj.assigned_to === 'string' ? JSON.parse(proj.assigned_to) : proj.assigned_to
        teamLeader = a?.team_leader || ''
        assignedTo = a?.sales_person || teamLeader
      } catch {}
    }
    if (Array.isArray(proj.team_members) && proj.team_members.length > 0) {
      const engMember = proj.team_members.find((m: any) => m.role === 'engineer' || m.role === 'chief_engineer')
      engineer = engMember?.full_name || proj.team_members[0]?.full_name || ''
    }
    setForm((prev: any) => ({
      ...prev,
      project_id: proj.id,
      project_name: proj.client_name ? `${proj.project_type}-${proj.project_code} (${proj.client_name})` : `${proj.project_type}-${proj.project_code}`,
      project_code: proj.project_code,
      project_type: proj.project_type,
      client_name: proj.client_name || '',
      phone: proj.phone || '',
      address: proj.address || '',
      description: proj.description || prev.description,
      team_leader: teamLeader,
      engineer: engineer,
      assigned_to: assignedTo,
    }))
  }

  const handleSubmitCreate = () => {
    setError('')
    createMutation.mutate(form)
  }

  const handleSubmitEdit = () => {
    if (!selected) return
    setError('')
    updateMutation.mutate({ id: selected.id, payload: form })
  }

  const handleDownloadPdf = async () => {
    if (!selected) return
    setDownloadingPdf(true)
    try {
      const token = localStorage.getItem('token')
      const url = `/api/handovers/${selected.id}/pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`
      window.open(url, '_blank')
    } finally {
      setDownloadingPdf(false)
    }
  }

  const f = (key: string) => (e: any) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((prev: any) => ({ ...prev, [key]: val }))
  }

  const formValid = form.project_name?.trim() && form.client_name?.trim()

  const handovers: HandoverForm[] = data?.data || []
  const total = data?.total || 0
  const totalPages = data?.total_pages || 1

  const allChecked = selected ? (selected.manual_complete && selected.products_checklist && selected.customer_signed) : false
  const canMarkComplete = selected?.status === 'Approved' && allChecked

  const linkedProjectLabel = selected?.project_id
    ? `${selected.project_type}-${selected.project_code} — ${selected.client_name}`
    : selected?.project_code
      ? `${selected.project_type}-${selected.project_code} — ${selected.client_name} (unlinked)`
      : null

  const editLinkedProjectLabel = form.project_id
    ? `${form.project_type}-${form.project_code} — ${form.client_name}`
    : null

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={22} className="text-red-700" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Handover Forms</h1>
          {total > 0 && <span className="text-sm text-slate-500">({total})</span>}
        </div>
        {canWrite && (
        <button onClick={handleOpenCreate}
          className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 transition-colors">
          <Plus size={16} /> New Handover
        </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search by HO#, project, client…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-red-800" />
        </div>
      ) : handovers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <ClipboardCheck size={36} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No handover forms found</p>
          <button onClick={handleOpenCreate} className="mt-4 text-sm text-red-800 hover:underline">
            Create your first handover form
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">HO#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Checklist</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {handovers.map(h => {
                  const checkedCount = [h.manual_complete, h.products_checklist, h.customer_signed].filter(Boolean).length
                  return (
                    <tr key={h.id} onClick={() => { setSelected(h); setEditMode(false); setError(''); setTransferSuccess('') }}
                      className="hover:bg-slate-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3 font-medium text-red-800">
                        <div className="flex items-center gap-1.5">
                          {h.ho_number}
                          {h.project_id && <Link2 size={12} className="text-slate-400" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[180px]">{h.project_name || '—'}</div>
                        {h.project_code && <div className="text-xs text-slate-400">{h.project_type}-{h.project_code}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 hidden sm:table-cell truncate max-w-[140px]">{h.client_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{h.project_type}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${checkedCount === 3 ? 'bg-green-500' : checkedCount > 0 ? 'bg-amber-400' : 'bg-slate-300'}`} />
                          <span className="text-xs text-slate-500">{checkedCount}/3</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={h.status} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1 text-xs border rounded-lg disabled:opacity-40 hover:bg-slate-50">Prev</button>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 text-xs border rounded-lg disabled:opacity-40 hover:bg-slate-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Slide-over */}
      {selected && !editMode && !showCreate && (
        <SlideOver open={!!selected} onClose={() => { setSelected(null); setTransferConfirm(false); setError(''); setTransferSuccess('') }}
          title={selected.ho_number}>
          <div className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-800 text-sm">
                <AlertCircle size={16} /> {error}
              </div>
            )}
            {transferSuccess && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-800 text-sm">
                <CheckCircle2 size={16} /> {transferSuccess}
              </div>
            )}

            {/* Status + Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={selected.status} />
              <div className="ml-auto flex items-center gap-2">
                <button onClick={handleDownloadPdf} disabled={downloadingPdf}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                  {downloadingPdf ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  PDF
                </button>
                {canWrite && (
                <button onClick={handleOpenEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors">
                  Edit
                </button>
                )}
                {isAdmin && (
                  <button onClick={() => deleteMutation.mutate(selected.id)} disabled={deleteMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 text-red-700 rounded-lg hover:bg-red-50 transition-colors">
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Linked Project */}
            {linkedProjectLabel ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <Building2 size={14} className="text-blue-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-blue-500 font-medium">Linked Project</div>
                  <div className="text-sm font-medium text-blue-800 truncate">{linkedProjectLabel}</div>
                </div>
                {!selected.project_id && (
                  <span className="text-xs text-orange-500 shrink-0">Not linked by ID</span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                <Building2 size={14} className="text-slate-400 shrink-0" />
                <div className="text-xs text-slate-400">No project linked — edit to link a project from Project Bank</div>
              </div>
            )}

            {/* Info Sections */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Project Name', value: selected.project_name },
                { label: 'Project Code', value: selected.project_code ? `${selected.project_type}-${selected.project_code}` : '' },
                { label: 'Client Name', value: selected.client_name },
                { label: 'Phone', value: selected.phone },
                { label: 'Engineer', value: selected.engineer },
                { label: 'Team Leader', value: selected.team_leader },
                { label: 'Handover Date', value: selected.handover_date },
                { label: 'Date Completed', value: selected.date_completed },
                { label: 'Service Expiry', value: selected.service_expiry_date },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div className="text-xs text-slate-400 mb-0.5">{label}</div>
                  <div className="text-sm font-medium text-slate-900">{value || '—'}</div>
                </div>
              ))}
            </div>

            {selected.address && (
              <div>
                <div className="text-xs text-slate-400 mb-0.5">Address</div>
                <div className="text-sm text-slate-700">{selected.address}</div>
              </div>
            )}

            {selected.description && (
              <div>
                <div className="text-xs text-slate-400 mb-1">Description</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{selected.description}</div>
              </div>
            )}

            {/* Checklist */}
            <div className="border border-slate-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Handover Checklist</h4>
              <div className="space-y-2">
                <CheckItem label="Manual Handover Complete" checked={selected.manual_complete} />
                <CheckItem label="Products Checklist Verified" checked={selected.products_checklist} />
                <CheckItem label="Customer Signature Obtained" checked={selected.customer_signed} />
              </div>
            </div>

            {/* Attachments */}
            <HandoverAttachments handoverId={selected.id} canDelete={isAdmin || ENGINEERING_ROLES.includes(user?.role || '')} />

            {selected.notes && (
              <div>
                <div className="text-xs text-slate-400 mb-1">Notes</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{selected.notes}</div>
              </div>
            )}

            {/* Mark Project as Complete */}
            {canMarkComplete && !transferSuccess && (
              <div className="border border-green-200 bg-green-50 rounded-xl p-4">
                <div className="text-sm font-semibold text-green-800 mb-1">Ready to Complete</div>
                <div className="text-xs text-green-700 mb-3">
                  All checklist items are done and this handover is approved. Click below to mark the linked project as Complete in Project Bank.
                </div>
                {!transferConfirm ? (
                  <button onClick={() => setTransferConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm rounded-lg hover:bg-green-800 transition-colors">
                    <CheckCircle2 size={14} /> Mark Project as Complete
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-green-700">This will update the project status to Complete in Project Bank. Confirm?</span>
                    <button onClick={() => transferMutation.mutate(selected.id)} disabled={transferMutation.isPending}
                      className="px-3 py-1.5 bg-green-700 text-white text-xs rounded-lg hover:bg-green-800 whitespace-nowrap">
                      {transferMutation.isPending ? 'Updating…' : 'Yes, Mark Complete'}
                    </button>
                    <button onClick={() => setTransferConfirm(false)} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-slate-50 whitespace-nowrap">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </SlideOver>
      )}

      {/* Create / Edit Slide-over */}
      {(showCreate || editMode) && (
        <SlideOver
          open={showCreate || editMode}
          onClose={() => { setShowCreate(false); setEditMode(false); setError('') }}
          title={showCreate ? 'New Handover Form' : `Edit ${selected?.ho_number}`}
        >
          <HandoverFormFields
            form={form}
            f={f}
            isAdmin={isAdmin}
            onProjectSelect={handleProjectSelect}
          />
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-800 text-sm mt-4">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          <div className="flex gap-2 pt-4 mt-4 border-t border-slate-100">
            <button
              disabled={!formValid || createMutation.isPending || updateMutation.isPending}
              onClick={showCreate ? handleSubmitCreate : handleSubmitEdit}
              className="flex-1 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50 transition-colors">
              {(createMutation.isPending || updateMutation.isPending) ? (
                <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Saving…</span>
              ) : showCreate ? 'Create Handover' : 'Save Changes'}
            </button>
            <button onClick={() => { setShowCreate(false); setEditMode(false); setError('') }}
              className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors">
              Cancel
            </button>
          </div>
        </SlideOver>
      )}
    </div>
  )
}

function HandoverFormFields({ form, f, isAdmin, onProjectSelect }: {
  form: any
  f: Function
  isAdmin: boolean
  onProjectSelect: (proj: any) => void
}) {
  const inp = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700'
  const lbl = 'block text-xs font-medium text-slate-600 mb-1'
  const readonlyInp = 'w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-600 cursor-not-allowed'
  const STATUSES_ADMIN = ['Draft', 'Pending', 'Approved', 'Rejected']
  const STATUSES_USER = ['Draft', 'Pending']

  const hasProject = !!form.project_id

  return (
    <div className="space-y-4">
      {/* Project Picker */}
      <div>
        <label className={lbl}>
          <span className="flex items-center gap-1">
            <Building2 size={12} /> Link to Project Bank
          </span>
        </label>
        <ProjectPicker
          value={{
            id: form.project_id,
            label: form.project_id
              ? `${form.project_type}-${form.project_code} — ${form.client_name}`
              : '',
          }}
          onSelect={onProjectSelect}
        />
        {hasProject && (
          <p className="text-xs text-slate-400 mt-1">
            Project fields are auto-filled from Project Bank. Edit them directly in Project Bank if needed.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Project info — read-only if linked, editable otherwise */}
        <div className="col-span-2">
          <label className={lbl}>Project Name <span className="text-red-500">*</span></label>
          {hasProject ? (
            <div className={readonlyInp}>{form.project_name || '—'}</div>
          ) : (
            <input className={inp} value={form.project_name} onChange={f('project_name')} placeholder="e.g. Office Installation" />
          )}
        </div>
        <div>
          <label className={lbl}>Project Code</label>
          {hasProject ? (
            <div className={readonlyInp}>{form.project_code || '—'}</div>
          ) : (
            <input className={inp} value={form.project_code} onChange={f('project_code')} placeholder="e.g. FM-001" />
          )}
        </div>
        <div>
          <label className={lbl}>Project Type</label>
          {hasProject ? (
            <div className={readonlyInp}>{form.project_type}</div>
          ) : (
            <select className={inp} value={form.project_type} onChange={f('project_type')}>
              {['CLAPJ', 'FMTPJ', 'ELVPJ'].map(t => <option key={t}>{t}</option>)}
            </select>
          )}
        </div>
        <div className="col-span-2">
          <label className={lbl}>Client Name <span className="text-red-500">*</span></label>
          {hasProject ? (
            <div className={readonlyInp}>{form.client_name || '—'}</div>
          ) : (
            <input className={inp} value={form.client_name} onChange={f('client_name')} placeholder="Client or company name" />
          )}
        </div>
        <div>
          <label className={lbl}>Phone</label>
          {hasProject ? (
            <div className={readonlyInp}>{form.phone || '—'}</div>
          ) : (
            <input className={inp} value={form.phone} onChange={f('phone')} />
          )}
        </div>
        <div>
          <label className={lbl}>Status</label>
          <select className={inp} value={form.status} onChange={f('status')}>
            {(isAdmin ? STATUSES_ADMIN : STATUSES_USER).map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className={lbl}>Address</label>
          {hasProject ? (
            <div className={readonlyInp}>{form.address || '—'}</div>
          ) : (
            <input className={inp} value={form.address} onChange={f('address')} />
          )}
        </div>
        <div>
          <label className={lbl}>Engineer</label>
          {hasProject ? (
            <div className={readonlyInp}>{form.engineer || '—'}</div>
          ) : (
            <input className={inp} value={form.engineer} onChange={f('engineer')} placeholder="Engineer name" />
          )}
        </div>
        <div>
          <label className={lbl}>Team Leader</label>
          {hasProject ? (
            <div className={readonlyInp}>{form.team_leader || '—'}</div>
          ) : (
            <input className={inp} value={form.team_leader} onChange={f('team_leader')} placeholder="Team leader name" />
          )}
        </div>
        <div>
          <label className={lbl}>Handover Date</label>
          <input type="date" className={inp} value={form.handover_date} onChange={f('handover_date')} />
        </div>
        <div>
          <label className={lbl}>Date Completed</label>
          <input type="date" className={inp} value={form.date_completed} onChange={f('date_completed')} />
        </div>
        <div>
          <label className={lbl}>Service Expiry Date</label>
          <input type="date" className={inp} value={form.service_expiry_date} onChange={f('service_expiry_date')} />
        </div>
        <div className="col-span-2">
          <label className={lbl}>Description / Notes from Project</label>
          <textarea rows={3} className={inp} value={form.description} onChange={f('description')}
            placeholder="Additional project description or handover context" />
        </div>

        {/* Checklist */}
        <div className="col-span-2">
          <div className="text-xs font-medium text-slate-600 mb-2">Handover Checklist</div>
          <div className="space-y-2">
            {[
              { key: 'manual_complete', label: 'Manual Handover Complete' },
              { key: 'products_checklist', label: 'Products Checklist Verified' },
              { key: 'customer_signed', label: 'Customer Signature Obtained' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form[key] || false} onChange={f(key)}
                  className="w-4 h-4 rounded border-slate-300 text-red-800 focus:ring-red-700" />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="col-span-2">
          <label className={lbl}>Notes</label>
          <textarea rows={3} className={inp} value={form.notes} onChange={f('notes')}
            placeholder="Additional notes for this handover" />
        </div>
      </div>
    </div>
  )
}
